import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  PermissionGroup,
  ProjectWorkspaceList,
} from '../../types/permission-group/permissionGroup';
import {buildClientPermissionMap} from '../../utils/permissionsUtils';
import {logoutAction} from '../rootAction';

// Define the UserRole interfaces
export type PermissionsByClient = Record<string, Record<string, boolean>>;

interface Client {
  clientId: string;
  name: string;
  budgetConsumptionEnabled?: boolean;
}

interface Role {
  roleId: string;
  name: string;
  description: string;
  roleCode: string;
}

interface UserRolePermissionGroup {
  userRolePermissionGroupId: string;
  permissionGroup: PermissionGroup;
}

export interface UserRolesData {
  client: Client;
  createdBy: string;
  createdTime: string;
  projectWorkspaceList: ProjectWorkspaceList[];
  role: Role;
  updatedBy: string;
  updatedTime: string;
  userId: string;
  userRoleId: string;
  userRolePermissionGroup: UserRolePermissionGroup[];
}

interface UserRoleState {
  userRoles: UserRolesData[];
  loggedInUserRoles: UserRolesData[];
  permissionsByClient: PermissionsByClient;
  userRoleForSelectedClient?: string;
}
const initialState: UserRoleState = {
  userRoles: [],
  loggedInUserRoles: [],
  permissionsByClient: {},
  userRoleForSelectedClient: '',
};

const userRoleSlice = createSlice({
  name: 'userRole',
  initialState,
  reducers: {
    setUserRolesData(state, action: PayloadAction<UserRolesData[]>) {
      state.userRoles = action.payload;
    },
    setLoggedInUserRoles(state, action: PayloadAction<UserRolesData[]>) {
      state.loggedInUserRoles = action.payload;
      state.permissionsByClient = buildClientPermissionMap(action.payload);
    },
    upsertUserRole(state, action: PayloadAction<UserRolesData>) {
      const index = state.userRoles.findIndex(
        (role) => role.userRoleId === action.payload.userRoleId
      );
      if (index >= 0) {
        state.userRoles[index] = action.payload;
      } else {
        state.userRoles.push(action.payload);
      }
    },
    removeUserRole(state, action: PayloadAction<string>) {
      state.userRoles = state.userRoles.filter(
        (role) => role.userRoleId !== action.payload
      );
    },
    setUserRoleForSelectedClient(state, action: PayloadAction<string>) {
      state.userRoleForSelectedClient = action.payload;
    },
    resetUserRoles(state) {
      state.userRoles = [];
      state.userRoleForSelectedClient = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => {
      return initialState;
    });
  },
});
export const {
  setUserRolesData,
  setLoggedInUserRoles,
  upsertUserRole,
  removeUserRole,
  setUserRoleForSelectedClient,
  resetUserRoles,
} = userRoleSlice.actions;

export default userRoleSlice.reducer;
